import { TamediaStyles } from "@ta-interaktiv/microfrontend-template-helpers";
import React, { useState, useEffect } from 'react'
import styled, { ThemeProvider } from 'styled-components'

import { TextWidth } from '@ta-interaktiv/react-disco-grid'
import { GlobalVariables } from 'Globals/GlobalVariables'
import TVShow from 'components/TVShow'

// To test emojis
import emoji from './code_sentence_emoji.json'

const FormatWeather = (weather: {
  emoji: string
  sentence: string
  bold: string
}) => {
  return (
    <>
      {weather.emoji} {weather.sentence.split(weather.bold)[0]}
      <B>{weather.bold}</B>
      {weather.sentence.split(weather.bold)[1]}
    </>
  )
}

const FormatTPG = ({
  sentences,
  bullets,
  gpt_bullets,
  usesChatGPT,
}: {
  sentences: string
  bullets: string[]
  gpt_bullets: string
  usesChatGPT: boolean
}) => {
  if (usesChatGPT) {
    sentences = sentences.replace('impactent', 'affectent')
  }
  let prefix = '🚌'
  const tpgPosition = sentences.indexOf('TPG')
  const linkStartPosition = tpgPosition + 4
  const linkEndPosition = sentences.indexOf('.')
  const sentenceStart = sentences.slice(0, tpgPosition)
  const link = sentences.slice(linkStartPosition, linkEndPosition)
  const sentenceEnd = sentences.slice(linkEndPosition)

  return (
    <>
      {prefix} {sentenceStart}
      <B>TPG</B>{' '}
      <a
        target='_blank'
        rel='noreferrer'
        href='https://www.tpg.ch/fr/voyager/info-trafic/info-trafic'
      >
        {link}
      </a>
      {sentenceEnd}
    </>
  )
}

const FormatRoadWorks = ({ sentences }: { sentences: string }) => {
  const keyword = sentences.includes('chantiers') ? 'chantiers' : 'chantier'
  const keywordPosition = sentences.indexOf(keyword)
  const sentenceStart = sentences.slice(0, keywordPosition)
  const sentenceEnd = sentences.slice(keywordPosition + keyword.length)
  return (
    <P>
      🚧 {sentenceStart}
      <B>{keyword}</B>
      {sentenceEnd} Retrouvez les principaux chantiers perturbant le trafic sur
      notre{' '}
      <a
        href='https://www.tdg.ch/288391167813?utm_source=geneva-today'
        target='_blank'
        rel='noreferrer'
      >
        carte des chantiers
      </a>
      .
    </P>
  )
}

const MainComponent = ({
  tenant,
  colorMode,
  properties = {},
  trackEvent,
}: MicroFrontendProps) => {
  const [data, setData] = useState<any | null>(null)
  useEffect(() => {
    fetch(
      'https://storage.googleapis.com/wch-interactive/geneva-today/latest.json?' +
        Math.random()
    )
      .then((res) => res.json())
      .then((data) => setData(data))
  }, [])

  const theme = {
    colorMode: colorMode,
    secondaryButtonBackground:
      colorMode === 'dark'
        ? 'var(--brandblue-bright-2)'
        : 'var(--beige-bright-2)',
    secondaryButtonText: colorMode === 'dark' ? '#ffffff' : 'var(--text-color)',
    primaryButtonBackground: 'var(--text-color)',
    primaryButtonText:
      colorMode === 'dark' ? 'var(--brandblue-main)' : '#ffffff',
    coloredBackground:
      colorMode === 'dark'
        ? 'var(--brandblue-bright-1)'
        : 'var(--beige-bright-3)',
    tenant: tenant ?? { key: '24heures', name: '24heures' },
  }

  return (
    <AppContainer $colorMode={colorMode}>
      <ThemeProvider theme={theme}>
        <TextWidth>
          {data && (
            <>
              <P>
                {properties.weather ? (
                  properties.weather
                ) : (
                  <FormatWeather {...data.weather} />
                )}
              </P>
              {data.delay_sentences &&
                (properties.delay ? (
                  properties.delay
                ) : (
                  <P>
                    {data.delay_sentences.split('bouchonne')[0]}
                    <B>bouchonne</B>
                    {data.delay_sentences.split('bouchonne')[1]}
                  </P>
                ))}
              {data.road_works_sentence && (
                <FormatRoadWorks
                  sentences={
                    properties.road_works
                      ? properties.road_works
                      : data.road_works_sentence
                  }
                />
              )}
              <P>
                {properties.tpg ? (
                  properties.tpg
                ) : (
                  <FormatTPG
                    sentences={
                      data.tpg_gpt_sentence
                        ? properties.nogpt
                          ? data.tpg_sentences
                          : data.tpg_gpt_sentence
                        : data.tpg_sentences
                    }
                    bullets={data.tpg_bullets_sentences}
                    gpt_bullets={data.tpg_bullets_gpt_sentence}
                    usesChatGPT={data.tpg_gpt_sentence ? true : false}
                  />
                )}
              </P>
              <P>
                <B>🍽️ Un resto ou snack ce midi?</B> Nous vous proposons{' '}
                {data.restaurant.determinant} {data.restaurant.name}, que nous
                avons{' '}
                <a
                  target='_blank'
                  href={
                    'https://' +
                    (properties?.tenant || 'tdg') +
                    '.ch/' +
                    data.restaurant.story_id
                  }
                  rel='noreferrer'
                >
                  {data.restaurant.genre && data.restaurant.genre === 'f'
                    ? 'testée'
                    : 'testé'}{' '}
                  pour vous
                </a>
                . Au menu: de la cuisine {data.restaurant.kitchen}.
              </P>

              {/* <img
                src='https://cdn.unitycms.io/images/8eFzWXAOaDS9qvPCJdpHzz.jpg?op=focus&val=600,300,1000,1000,0,0,500,500&sum=hcOZJJMdRt4'
                alt='test'
                style={{ width: '100%' }}
              /> */}

              {data.tv_show && (
                <>
                  <P style={{ marginBottom: 0 }}>
                    📺 A court d’idées de <B>{data.tv_show.type}</B>? Notre
                    rédaction{' '}
                    <a
                      href={`https://www.${theme.tenant.key}.ch/fan-de-series-streaming-956521001816?serie=${data.tv_show.id}`}
                      target='_blank'
                      rel='noreferrer'
                    >
                      donne une note de {data.tv_show.rating} sur 5
                    </a>{' '}
                    à <B>{data.tv_show.name}</B>, disponible{' '}
                    <a
                      href={data.tv_show.provider[0].url}
                      target='_blank'
                      rel='noreferrer'
                    >
                      sur {data.tv_show.provider[0].name}
                    </a>
                    .
                  </P>
                  <TVShow {...data.tv_show} />
                </>
              )}
            </>
          )}
          <P>Bonne journée!</P>
        </TextWidth>
        {window.location.hostname === 'localhost' && (
          <TextWidth>
            <hr />
            <h3>Débug</h3>
            <>
              {Object.values(emoji).map((item, index) => (
                <P key={`${index}_${item.sentence_fr.replace(/ /g, '-')}`}>
                  {item.emoji} {item.sentence_fr.split(item.bold)[0]}
                  <B>{item.bold}</B>
                  {item.sentence_fr.split(item.bold)[1]}
                </P>
              ))}
            </>
          </TextWidth>
        )}
      </ThemeProvider>
    </AppContainer>
  )
}
const AppContainer = styled(TamediaStyles)<{ $colorMode: 'dark' | 'light' }>`${GlobalVariables}
  font-family: var(--font-sans-serif);
  font-size: 1.8rem;
  line-height: 3rem;
  a {
    color: inherit;
    text-decoration: none;
    padding: 0.2rem 0 0;
    background-image: linear-gradient(
      to right,
      var(
          ${(props) =>
            props.$colorMode === 'dark'
              ? '--brandblue-bright-3'
              : '--yellow-main'}
        )
        0,
      var(
          ${(props) =>
            props.$colorMode === 'dark'
              ? '--brandblue-bright-3'
              : '--yellow-main'}
        )
        100%
    );
    background-size: 100% 20%;
    background-position-y: 17px;
    background-repeat: repeat-x;
    background-position: bottom;
  }
  ${({ theme }) =>
    theme.colorMode === 'dark' &&
    'background: linear-gradient(180deg, hsla(0, 0%, 7%, 0), #222222);'}
`
const IntroP = styled.p`
  font-size: 0.9em;
  font-weight: bold;
  background-color: var(--gray-6);
  padding: 0.5rem 2rem;
  ${({ theme }) =>
    theme.colorMode === 'dark' &&
    `background: var(--gray-5);
    color: var(--brandblue-main);`}
`
const P = styled.p``
const B = styled.b`
  // font-size: 3rem;
`
export default MainComponent
